import { useCallback, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { fetchQuery, graphql } from "react-relay"
import { uniqBy } from "es-toolkit/array"
import { debounce } from "es-toolkit/function"
import { Input } from "@/components/ui/input"
import { postalCodeSelectorGetPostalCodesQuery } from "@/modules/inbound-wizard/components/contact/address/__generated__/postalCodeSelectorGetPostalCodesQuery.graphql"
import {
  SelectBoxOption,
  SelectBoxWithSearch,
} from "@/modules/inbound-wizard/components/contact/address/selectbox-with-search"
import { RelayEnvironment } from "@/relay-environment"

export type PostalCodeData = SelectBoxOption & { city?: string }

type PostalCodeSelectorValueProps = {
  value: string
  onChange?: (value: string | undefined) => void
  error: string | undefined
  onSelect: (value?: PostalCodeData) => void
  ref?: React.Ref<HTMLInputElement>
  searchInputIdPrefix?: string
  className?: React.HTMLAttributes<HTMLDivElement>["className"]
  label?: string
}

export type PostalCodeSelectorProps = PostalCodeSelectorValueProps & {
  countryIsoCode: string
  postalCode?: PostalCodeData
}

export const PostalCodeSelector = (props: PostalCodeSelectorProps) => {
  const { t } = useTranslation()
  const {
    postalCode,
    countryIsoCode,
    value,
    onChange,
    error,
    onSelect,
    ref,
    searchInputIdPrefix,
    label = `${t("common.postalCode")}*`,
  } = props
  const testId = "patient-postal-code"

  const [postalCodes, setPostalCodes] = useState<ReadonlyArray<PostalCodeData>>([])

  const handleSearchPostalCode = useCallback(async (keyword: string, countryIsoCode: string) => {
    const response = await fetchQuery<postalCodeSelectorGetPostalCodesQuery>(
      RelayEnvironment,
      graphql`
        query postalCodeSelectorGetPostalCodesQuery($countryIsoCode: String!, $keyword: String) {
          displayablePostalCodes(countryIsoCode: $countryIsoCode, keyword: $keyword) {
            label
            value
            city
          }
        }
      `,
      { countryIsoCode: countryIsoCode, keyword },
      { fetchPolicy: "store-or-network" },
    ).toPromise()

    setPostalCodes((previous) => response?.displayablePostalCodes ?? previous)
  }, [])

  const debounceSearchPostalCode = useRef(
    debounce((keyword: string, countryIsoCode: string) => handleSearchPostalCode(keyword, countryIsoCode), 250),
  ).current
  const isSwitzerlandSelected = countryIsoCode === "CH"

  useEffect(() => {
    if (isSwitzerlandSelected) {
      handleSearchPostalCode("", countryIsoCode)
    } else {
      setPostalCodes([])
    }
  }, [countryIsoCode, handleSearchPostalCode, isSwitzerlandSelected])

  const mergedOptions = postalCode ? uniqBy(postalCodes.concat(postalCode), (item) => item.value) : postalCodes
  const errorMsg = t(error ?? "")

  useEffect(() => {
    if (countryIsoCode) {
      debounceSearchPostalCode(value, countryIsoCode)
    }
  }, [value, countryIsoCode, debounceSearchPostalCode])

  if (!isSwitzerlandSelected)
    return (
      <Input
        wrapperProps={{ className: props.className }}
        ref={ref}
        label={label}
        error={errorMsg}
        onChange={(e) => onChange?.(e.target.value)}
        value={value}
        testId={testId}
      />
    )

  return (
    <SelectBoxWithSearch
      className={props.className}
      ref={ref}
      type={searchInputIdPrefix ?? "postalCode"}
      options={mergedOptions}
      value={value ?? ""}
      placeholder={label}
      error={errorMsg}
      testId={testId}
      onChange={(option?: PostalCodeData) => {
        onChange?.(option?.value)
        onSelect(option)
      }}
      onSearchChange={(val) => {
        onChange?.(val)
        debounceSearchPostalCode(val, countryIsoCode)
      }}
      renderOption={(option: PostalCodeData) => (
        <div className="grid grid-cols-1 py-0.5">
          <div className="text-body-sm text-on-surface block truncate">{option.label}</div>
          <div className="text-body-sm order-1 block truncate">{option.city}</div>
        </div>
      )}
      ignoreLocalFilter
    />
  )
}
